/**
 * Every control kind that exists, one import and one entry each, keyed by the kind it answers to.
 *
 * A lookup rather than a `switch (control.kind)` in the screen, for the reason the repo bans that
 * shape everywhere else: a branch every new kind has to edit is a branch somebody forgets to edit.
 * A knob names a kind, this map answers with the file that owns it, and neither the screen nor the
 * validator learns what a slider is.
 *
 * There are four kinds. Each one after the first arrived as a file and a line here, which is what
 * the map was for: neither the screen nor the validator learned that a choice exists, that one kind
 * of slider can play the sound it sets, or that a switch has no value to tune at all.
 */

import { CHOICE_BUTTONS } from '@/ui/settings/controls/choiceButtons';
import { KEY_BINDING_ROWS } from '@/ui/settings/controls/keyBindingRows';
import { LEVEL_SLIDER } from '@/ui/settings/controls/levelSlider';
import { RANGE_SLIDER } from '@/ui/settings/controls/rangeSlider';
import { TOGGLE_SWITCH } from '@/ui/settings/controls/toggleSwitch';
import type { SettingControlDefinition, SettingControlKind } from '@/ui/settings/settingContract';

const CONTROLS: readonly SettingControlDefinition[] = [
  RANGE_SLIDER,
  CHOICE_BUTTONS,
  LEVEL_SLIDER,
  TOGGLE_SWITCH,
  KEY_BINDING_ROWS,
];

const BY_KIND = new Map<SettingControlKind, SettingControlDefinition>(
  CONTROLS.map((control) => [control.kind, control]),
);

/**
 * The file that owns a kind.
 *
 * Throws when nothing does, which cannot happen with a registered kind: `SettingControlKind` is
 * derived from the control union, so a kind with no entry is a build that never shipped rather than
 * a screen that renders an empty row.
 */
export function controlFor(kind: SettingControlKind): SettingControlDefinition {
  const control = BY_KIND.get(kind);
  if (control === undefined) throw new Error(`settings: no control registered for ${kind}`);
  return control;
}
